import { Video } from 'lucide-react'
import React, { useCallback, useEffect, useRef, useState } from 'react'
import { useNavigate } from 'react-router-dom'
import BotContainerIcon from '@/assets/bot-container-icon.webp'
import BotErrorImage from '@/assets/bot-error-image.webp'
import { Button, Column, Row, Text } from '@/components/core'
import AvatarCard from '@/components/layout/AvatarCard.tsx'
import { useMediaDevices } from '@/contexts/MediaDevicesContext.tsx'
import { ROUTES } from '@/types/routes.ts'

const VideoTest: React.FC = () => {
  const navigate = useNavigate()
  const { hasVideoPermission, isCheckingPermission, requestVideoPermission } =
    useMediaDevices()

  const videoRef = useRef<HTMLVideoElement>(null)
  const videoMediaStream = useRef<MediaStream>(null)

  const [alreadyAsked, setAlreadyAsked] = useState(false)

  const handleEnableCamera = useCallback(async () => {
    setAlreadyAsked(true)
    await requestVideoPermission()
  }, [requestVideoPermission])

  useEffect(() => {
    if (hasVideoPermission) {
      navigator.mediaDevices
        .getUserMedia({
          audio: true,
          video: true
        })
        .then((stream) => {
          videoMediaStream.current = stream
          if (videoRef.current) videoRef.current.srcObject = stream
        })
    }

    return () => {
      if (videoMediaStream.current) {
        videoMediaStream.current.getTracks().forEach((track) => track.stop())
      }
    }
  }, [hasVideoPermission])

  if (!hasVideoPermission && !isCheckingPermission && alreadyAsked) {
    return (
      <AvatarCard variant="image" botAvatar={BotErrorImage}>
        <Column className="h-full w-full items-center">
          <Text.Translated
            color="600"
            align="center"
            className="max-sm:text-xl sm:text-2xl"
            translationKey="camera_disabled_video_test_error"
          />
          <div className="h-[16px]" />
          <Text.Translated
            color="600"
            align="center"
            className="max-sm:text-base"
            translationKey="camera_enable_instructions_video_test_error"
          />
          <div className="h-[48px]" />
          <Row className="w-full self-center sm:max-w-[222px]">
            <Button.Translated
              translationKey="reload_button_video_test_error"
              grow
              onClick={() => window.location.reload()}
            />
          </Row>
        </Column>
      </AvatarCard>
    )
  }

  return (
    <AvatarCard variant="icon" botAvatar={BotContainerIcon}>
      <Column className="h-full flex-1">
        <Column className="flex-1 items-center gap-y-4">
          <Column className="w-full items-center mt-[-10px] space-y-2">
            <Text
              color="600"
              align="center"
              className="max-sm:text-xl sm:text-2xl"
            >
              <Text.Translated
                color="600"
                fontWeight="bold"
                className="max-sm:text-xl sm:text-2xl"
                translationKey="video_check_title_video_test"
              />{' '}
              <Text.Translated translationKey="video_check_subtitle_video_test" />
            </Text>
            <Column className="items-center">
              <Text.Translated
                color="600"
                align="center"
                className="max-sm:text-base"
                translationKey="video_check_instruction_video_test"
              />
            </Column>
          </Column>
          <Column className="items-center justify-center h-[180px] w-[280px] mt-2 bg-gray-50 rounded-2xl overflow-hidden relative">
            <Column className="absolute rounded-full w-15 h-15 bg-gray-200 justify-center items-center">
              <Video className="text-gray-700 h-7 w-7" />
            </Column>
            <video
              ref={videoRef}
              autoPlay
              playsInline
              muted
              className="object-cover z-10"
            />
          </Column>
          <Column className="mx-16 w-full max-w-[360px] justify-center">
            {hasVideoPermission ? (
              <>
                <Button.Translated
                  onClick={() => navigate(ROUTES.ONBOARDING.TIMER_TIP)}
                  translationKey="next_button_video_test_done"
                />
                <Text.Translated
                  color="400"
                  align="center"
                  size="10"
                  className="mt-1"
                  translationKey="video_check_terms"
                />
              </>
            ) : (
              <Button.Translated
                translationKey="enable_camera_button_video_test"
                onClick={handleEnableCamera}
                pending={isCheckingPermission}
              />
            )}
          </Column>
        </Column>
      </Column>
    </AvatarCard>
  )
}

export default VideoTest
